import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Popup } from "semantic-ui-react";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import TableContainer from "../../common/ui/table-container/TableContainer";
import { Common } from "../../../utils";
import "../documents/Documents.css";
import "../../common/ui/data-table/DataTable.css";
import "./Attendance.css";
import {
  fetchDayActivity,
  fetchTodayActivity,
  handleAttendanceApiError,
  selfDeleteAttendanceEvent,
  selfEditAttendanceEvent,
  submitChangeRequest,
  sumClosedHours,
} from "./attendanceApi";
import AttendanceSelfEditModal from "./AttendanceSelfEditModal";
import { AttendanceGuideButton, AttendanceHelpModal } from "./AttendanceHelp";
import { fetchTimesheetProjects, mapTimesheetProjectsResponse } from "../timesheets/timesheetApi";
import {
  AttendanceChangeRequestType,
  AttendanceEventType,
  STANDARD_DAILY_WORK_HOURS,
  getPendingChangeRequestType,
  hasPendingAttendanceChangeRequest,
} from "./attendanceConstants";
import {
  addCalendarDaysToYmd,
  formatAttendanceTime,
  formatYmdInZone,
  getAttendanceDisplayTimeZone,
  parseUtcFromApi,
  parseYmd,
  toUtcIso,
  zonedCalendarDateStartUtc,
} from "./attendanceDateUtils";
import {
  bypassEmployeeEditDeleteOfficeFlags,
  noteTextWithoutCoordinates,
  parseCoordinatesFromAttendanceNote,
} from "./attendanceDailyLogsUtils";

/** @param {unknown} ev */
const eventKey = (ev) => {
  if (!ev || typeof ev !== "object") return "";
  const o = /** @type {{ id?: string, eventId?: string }} */ (ev);
  const id = o.id ?? o.eventId;
  return id != null ? String(id) : "";
};

/** @param {unknown} a @param {unknown} b */
const sameAttendanceEvent = (a, b) => {
  const ka = eventKey(a);
  const kb = eventKey(b);
  return ka !== "" && ka === kb;
};

const AttendanceDailyLogsPage = () => {
  const { t } = useTranslation();
  const [selectedYmd, setSelectedYmd] = useState(() => formatYmdInZone(Date.now(), getAttendanceDisplayTimeZone()));
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusyId, setActionBusyId] = useState(/** @type {string|null} */ (null));
  const [editModalEvent, setEditModalEvent] = useState(/** @type {object|null} */ (null));
  /** Rows from one activity group — opens documents-style delete confirmation */
  const [deleteModalGroupRows, setDeleteModalGroupRows] = useState(/** @type {object[] | null} */ (null));
  const [deleteModalBusy, setDeleteModalBusy] = useState(false);
  /** Pending correction on an event — withdraw via SubmitChangeRequest(Withdraw) */
  const [withdrawModalEv, setWithdrawModalEv] = useState(/** @type {object|null} */ (null));
  const [projects, setProjects] = useState(/** @type {{ id: string, name: string }[]} */ ([]));
  const [helpKey, setHelpKey] = useState(null);

  const displayTz = getAttendanceDisplayTimeZone();
  const isToday = useMemo(() => selectedYmd === formatYmdInZone(Date.now(), displayTz), [selectedYmd, displayTz]);

  const selectedDayAnchorUtc = useMemo(() => {
    const p = parseYmd(selectedYmd);
    if (!p) return null;
    return zonedCalendarDateStartUtc(p[0], p[1], p[2], displayTz);
  }, [selectedYmd, displayTz]);

  const load = useCallback(async () => {
    if (!selectedDayAnchorUtc) {
      setDayData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchDayActivity(toUtcIso(selectedDayAnchorUtc));
      setDayData(data);
    } catch (e) {
      const status = e?.response?.status;
      const todayYmd = formatYmdInZone(Date.now(), displayTz);
      if (status === 404 && selectedYmd === todayYmd) {
        try {
          const data = await fetchTodayActivity();
          setDayData(data);
        } catch (e2) {
          handleAttendanceApiError(e2, t("app.attendance.errors.loadToday"));
          setDayData(null);
        }
      } else {
        handleAttendanceApiError(e, t("app.attendance.errors.loadDay"));
        setDayData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedDayAnchorUtc, selectedYmd, displayTz, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchTimesheetProjects();
        const list = mapTimesheetProjectsResponse(raw);
        if (!cancelled) setProjects(list);
      } catch {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const events = dayData?.events ?? [];
  const segments = dayData?.segments ?? [];
  /** Backend: open single check-in day → direct SelfEdit API fails until check-out; correction request may still be used. */
  const openCheckInSessionOnly = Boolean(dayData?.openCheckInSessionOnly);
  const superAdminBypassOfficeFlags = bypassEmployeeEditDeleteOfficeFlags();
  const officeAllowsEmployeeEdit = Boolean(dayData?.allowUsersToEditSubmittedEvents);
  const officeAllowsEmployeeDelete = Boolean(dayData?.allowUsersToDeleteSubmittedEvents);
  /** Direct self-edit/delete APIs allowed (office flags or super admin without approver). */
  const useDirectEdit = (officeAllowsEmployeeEdit || superAdminBypassOfficeFlags) && !openCheckInSessionOnly;
  const useDirectDelete = officeAllowsEmployeeDelete || superAdminBypassOfficeFlags;
  const hidePunctuality = Boolean(dayData?.allowFlexibleWorkingWindow);

  const totalHoursDisplay = useMemo(() => {
    const api = dayData?.totalWorkedHours;
    if (api != null && Number.isFinite(Number(api))) return Number(api);
    return sumClosedHours(segments);
  }, [dayData?.totalWorkedHours, segments]);

  /** Prefer API overtime; if missing, anything over a standard 8h day counts as overtime. */
  const overtimeDisplay = useMemo(() => {
    const apiOt = dayData?.overtimeHours;
    if (apiOt != null && Number.isFinite(Number(apiOt))) return Number(apiOt);
    if (!Number.isFinite(totalHoursDisplay)) return null;
    return Math.max(0, totalHoursDisplay - STANDARD_DAILY_WORK_HOURS);
  }, [dayData?.overtimeHours, totalHoursDisplay]);

  const firstEvent = useMemo(() => {
    if (!events.length) return null;
    return [...events].sort(
      (a, b) =>
        (parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0) - (parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0)
    )[0];
  }, [events]);

  /** Latest check-out by time (hero “Last exit”, etc.). */
  const lastCheckOutEvent = useMemo(() => {
    const outs = events.filter((e) => e.eventType === AttendanceEventType.CheckOut);
    if (!outs.length) return null;
    return [...outs].sort(
      (a, b) =>
        (parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0) - (parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0)
    )[outs.length - 1];
  }, [events]);

  /** Earliest check-in on this day (punctuality vs 9:00 — same rule as Attendance Hub). */
  const firstCheckInEvent = useMemo(() => {
    const ins = events.filter((e) => e.eventType === AttendanceEventType.CheckIn);
    if (!ins.length) return null;
    return [...ins].sort(
      (a, b) =>
        (parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0) - (parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0)
    )[0];
  }, [events]);

  const punctualityKind = useMemo(() => {
    if (hidePunctuality) return null;
    if (!firstCheckInEvent?.occurredAtUtc) return null;
    const d = parseUtcFromApi(firstCheckInEvent.occurredAtUtc);
    if (!d) return null;
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: displayTz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const hour = Number(parts.find((p) => p.type === "hour")?.value ?? NaN);
    const minute = Number(parts.find((p) => p.type === "minute")?.value ?? NaN);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    const mins = hour * 60 + minute;
    const expectedStart = 9 * 60;
    return mins <= expectedStart ? "onTime" : "late";
  }, [firstCheckInEvent, displayTz, hidePunctuality]);

  const locationSummary = useMemo(() => {
    const sorted = [...events].sort(
      (a, b) =>
        (parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0) - (parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0)
    );
    for (let i = sorted.length - 1; i >= 0; i--) {
      const loc = sorted[i].location != null ? String(sorted[i].location).trim() : "";
      if (loc) return loc;
    }
    return null;
  }, [events]);

  /**
   * Groups of rows: each check-in → check-out pair is one group (2 rows, shared duration).
   * Orphan rows are single-row groups. Sorted chronologically for pairing, then groups reversed (latest first).
   */
  const activityGroups = useMemo(() => {
    const sorted = [...events].sort(
      (a, b) =>
        (parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0) - (parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0)
    );
    /** @type {Array<{ ev: (typeof sorted)[0], duration: string | null, durationRowSpan: number }>} */
    const out = [];
    let i = 0;
    while (i < sorted.length) {
      const ev = sorted[i];
      const next = sorted[i + 1];
      if (ev.eventType === AttendanceEventType.CheckIn && next && next.eventType === AttendanceEventType.CheckOut) {
        const t0 = parseUtcFromApi(ev.occurredAtUtc)?.getTime();
        const t1 = parseUtcFromApi(next.occurredAtUtc)?.getTime();
        let durationStr = "—";
        if (t0 != null && t1 != null && t1 > t0) durationStr = `${Math.round((t1 - t0) / 60000)}m`;
        out.push({
          ev,
          duration: durationStr,
          durationRowSpan: 2,
        });
        out.push({
          ev: next,
          duration: null,
          durationRowSpan: 0,
        });
        i += 2;
      } else {
        let durationStr = "—";
        if (ev.eventType === AttendanceEventType.CheckIn && next) {
          const t0 = parseUtcFromApi(ev.occurredAtUtc)?.getTime();
          const t1 = parseUtcFromApi(next.occurredAtUtc)?.getTime();
          if (t0 != null && t1 != null && t1 > t0) durationStr = `${Math.round((t1 - t0) / 60000)}m`;
        }
        out.push({
          ev,
          duration: durationStr,
          durationRowSpan: 1,
        });
        i += 1;
      }
    }
    const groups = [];
    for (let j = 0; j < out.length; ) {
      if (out[j].durationRowSpan === 2 && out[j + 1] && out[j + 1].durationRowSpan === 0) {
        groups.push([out[j], out[j + 1]]);
        j += 2;
      } else {
        groups.push([out[j]]);
        j += 1;
      }
    }
    return groups.reverse();
  }, [events]);

  /** Newest session (top group): single check-in row only — no direct edit on that block when paired with “first” rule below. */
  const newestSessionIsLoneCheckInOnly = useMemo(() => {
    const g = activityGroups[0];
    return Boolean(
      g && g.length === 1 && g[0].ev.eventType === AttendanceEventType.CheckIn
    );
  }, [activityGroups]);

  const dateHeroParts = useMemo(() => {
    const d = selectedDayAnchorUtc;
    if (!d) return null;
    const tz = displayTz;
    const weekday = new Intl.DateTimeFormat(undefined, { weekday: "long", timeZone: tz }).format(d);
    const calendar = new Intl.DateTimeFormat(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: tz,
    }).format(d);
    return { weekday, calendar };
  }, [selectedDayAnchorUtc, displayTz]);

  const lastMapCoords = useMemo(() => {
    if (!events.length) return null;
    const sortedDesc = [...events].sort(
      (a, b) =>
        (parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0) - (parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0)
    );
    for (const ev of sortedDesc) {
      const c = parseCoordinatesFromAttendanceNote(ev.note);
      if (c) return c;
    }
    return null;
  }, [events]);

  /**
   * One delete control per activity group. Single `SelfDelete` call: check-in event id for a pair (server removes the session);
   * otherwise the sole event’s id.
   * @param {Array<{ ev: object }>} groupRows
   */
  const executeDeleteGroup = async (groupRows) => {
    const rows = Array.isArray(groupRows) ? groupRows : [];
    const withIds = rows.map((r) => r.ev).filter((e) => eventKey(e));
    if (!withIds.length) return;

    const isPair =
      rows.length === 2 &&
      rows[0].ev.eventType === AttendanceEventType.CheckIn &&
      rows[1].ev.eventType === AttendanceEventType.CheckOut;

    const idToDelete = isPair ? eventKey(rows[0].ev) : eventKey(rows[0].ev);
    if (!idToDelete) return;

    setActionBusyId(String(idToDelete));
    try {
      await selfDeleteAttendanceEvent(idToDelete);
      Common.showSuccessMessage(t("app.attendance.dailyLogs.deleteEventSuccess"));
      await load();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        const serverMsg = e?.response?.data?.message ?? e?.response?.data?.Message;
        Common.showErrorMessage(
          typeof serverMsg === "string" && serverMsg.trim() ? serverMsg.trim() : t("app.attendance.dailyLogs.deleteEventNotFound")
        );
        await load();
      } else {
        handleAttendanceApiError(e, t("app.attendance.errors.clock"));
      }
    } finally {
      setActionBusyId(null);
    }
  };

  const handleDeleteModalConfirm = async () => {
    if (!deleteModalGroupRows || deleteModalBusy) return;
    if (useDirectDelete) {
      setDeleteModalBusy(true);
      try {
        await executeDeleteGroup(deleteModalGroupRows);
        setDeleteModalGroupRows(null);
      } finally {
        setDeleteModalBusy(false);
      }
      return;
    }
    const rows = Array.isArray(deleteModalGroupRows) ? deleteModalGroupRows : [];
    const isPair =
      rows.length === 2 &&
      rows[0].ev.eventType === AttendanceEventType.CheckIn &&
      rows[1].ev.eventType === AttendanceEventType.CheckOut;
    const idToRequest = isPair ? eventKey(rows[0].ev) : eventKey(rows[0].ev);
    if (!idToRequest) return;
    setDeleteModalBusy(true);
    setActionBusyId(String(idToRequest));
    try {
      await submitChangeRequest({
        eventId: String(idToRequest),
        requestType: AttendanceChangeRequestType.Delete,
      });
      Common.showSuccessMessage(t("app.attendance.dailyLogs.deleteRequestSubmitted"));
      setDeleteModalGroupRows(null);
      await load();
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.errors.loadRequests"));
    } finally {
      setDeleteModalBusy(false);
      setActionBusyId(null);
    }
  };

  const deleteModalRowsChrono = useMemo(() => {
    if (!deleteModalGroupRows) return [];
    return [...deleteModalGroupRows].sort(
      (a, b) =>
        (parseUtcFromApi(a.ev.occurredAtUtc)?.getTime() ?? 0) -
        (parseUtcFromApi(b.ev.occurredAtUtc)?.getTime() ?? 0)
    );
  }, [deleteModalGroupRows]);

  const handleEditOpen = (ev) => {
    const id = ev.id || ev.eventId;
    if (!id) return;
    setEditModalEvent(ev);
  };

  const handleEditSubmit = async (payload) => {
    const id = payload?.eventId ?? payload?.EventId;
    if (!id) return;
    setActionBusyId(String(id));
    try {
      if (useDirectEdit) {
        await selfEditAttendanceEvent(payload);
        Common.showSuccessMessage(t("app.attendance.dailyLogs.editEventSuccess"));
      } else {
        await submitChangeRequest({
          ...payload,
          eventId: String(id),
          requestType: AttendanceChangeRequestType.Edit,
        });
        Common.showSuccessMessage(t("app.attendance.dailyLogs.editRequestSubmitted"));
      }
      setEditModalEvent(null);
      await load();
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.errors.clock"));
    } finally {
      setActionBusyId(null);
    }
  };

  const confirmWithdrawChangeRequest = async () => {
    if (!withdrawModalEv) return;
    const id = eventKey(withdrawModalEv);
    if (!id) return;
    setActionBusyId(String(id));
    try {
      await submitChangeRequest({
        eventId: String(id),
        requestType: AttendanceChangeRequestType.Withdraw,
      });
      Common.showSuccessMessage(t("app.attendance.dailyLogs.withdrawRequestSuccess"));
      setWithdrawModalEv(null);
      await load();
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.errors.loadRequests"));
    } finally {
      setActionBusyId(null);
    }
  };

  const withdrawModalBusy = Boolean(
    withdrawModalEv && actionBusyId === eventKey(withdrawModalEv)
  );

  const colCount = 7;

  /**
   * @param {object[]} groupRows
   * @param {{ includeSpinner?: boolean }} [opts]
   */
  const renderDeleteButton = (groupRows, opts) => {
    const includeSpinner = opts?.includeSpinner !== false;
    const groupBusy = groupRows.some((r) => actionBusyId === eventKey(r.ev));
    const busy = groupBusy;
    const groupHasId = groupRows.some((r) => eventKey(r.ev));
    const groupPending = groupRows.some((r) => hasPendingAttendanceChangeRequest(r.ev));
    const pendingDeleteEv =
      groupRows.find((r) => getPendingChangeRequestType(r.ev) === AttendanceChangeRequestType.Delete)?.ev ??
      null;
    const withdrawDeleteKey = pendingDeleteEv ? eventKey(pendingDeleteEv) : "";
    const deleteDisabled = busy || !groupHasId || groupPending;
    const deletePopupMsg = busy
      ? null
      : groupPending
        ? t("app.attendance.dailyLogs.pendingChangeRequestBlocksActions")
        : !groupHasId
          ? t("app.attendance.dailyLogs.actionNoEventId")
          : null;
    const deleteBtn = (
      <button
        type="button"
        disabled={deleteDisabled}
        onClick={() => !deleteDisabled && setDeleteModalGroupRows(groupRows)}
        className="att-daily-pill-btn att-daily-pill-btn--delete"
      >
        {t("app.attendance.dailyLogs.deleteEvent")}
      </button>
    );
    const withdrawDeleteBtn = withdrawDeleteKey ? (
      <button
        type="button"
        className="att-daily-pill-btn att-daily-pill-btn--withdraw"
        disabled={busy || withdrawModalBusy || actionBusyId === withdrawDeleteKey}
        aria-label={t("app.attendance.dailyLogs.withdrawRequestAria")}
        onClick={() => setWithdrawModalEv(pendingDeleteEv)}
      >
        {t("app.attendance.dailyLogs.withdrawRequest")}
      </button>
    ) : null;
    return (
      <>
        {includeSpinner && busy && (
          <span className="spinner-border spinner-border-sm text-primary att-daily-action-busy" role="status" aria-hidden />
        )}
        {withdrawDeleteBtn}
        {deletePopupMsg ? (
          <Popup wide hoverable trigger={<span className="att-daily-action-trigger">{deleteBtn}</span>} content={deletePopupMsg} />
        ) : (
          deleteBtn
        )}
      </>
    );
  };

  /**
   * @param {object} ev
   * @param {{ showEdit: boolean, showDelete: boolean, groupRows: object[] }} opts
   */
  const renderActionButtons = (ev, opts) => {
    const { showEdit, showDelete, groupRows } = opts;
    const hasId = Boolean(eventKey(ev));
    const groupBusy = groupRows.some((r) => actionBusyId === eventKey(r.ev));
    const busy = groupBusy;

    if (!showEdit && !showDelete) {
      return <span className="text-muted">—</span>;
    }

    const rowPending = hasPendingAttendanceChangeRequest(ev);
    const pendingReqType = getPendingChangeRequestType(ev);
    const pendingShowsWithdrawByEdit =
      pendingReqType === AttendanceChangeRequestType.Edit ||
      pendingReqType == null ||
      pendingReqType === AttendanceChangeRequestType.None;
    const withdrawNextToEdit = rowPending && hasId && pendingShowsWithdrawByEdit;
    const withdrawKey = withdrawNextToEdit ? eventKey(ev) : "";
    const editDisabled = !hasId || busy || rowPending;
    const editPopupMsg = busy
      ? null
      : !hasId
        ? t("app.attendance.dailyLogs.actionNoEventId")
        : rowPending
          ? t("app.attendance.dailyLogs.pendingChangeRequestBlocksActions")
          : null;

    const editBtn = (
      <button
        type="button"
        disabled={editDisabled}
        onClick={() => !editDisabled && handleEditOpen(ev)}
        className="att-daily-pill-btn att-daily-pill-btn--edit"
      >
        {t("app.attendance.dailyLogs.editEvent")}
      </button>
    );

    const withdrawRowBtn = withdrawKey ? (
      <button
        type="button"
        className="att-daily-pill-btn att-daily-pill-btn--withdraw"
        disabled={busy || withdrawModalBusy || actionBusyId === withdrawKey}
        aria-label={t("app.attendance.dailyLogs.withdrawRequestAria")}
        onClick={() => setWithdrawModalEv(ev)}
      >
        {t("app.attendance.dailyLogs.withdrawRequest")}
      </button>
    ) : null;

    return (
      <div className="att-daily-action-btns">
        {busy && (
          <span className="spinner-border spinner-border-sm text-primary att-daily-action-busy" role="status" aria-hidden />
        )}
        {showEdit &&
          (editPopupMsg ? (
            <Popup wide hoverable trigger={<span className="att-daily-action-trigger">{editBtn}</span>} content={editPopupMsg} />
          ) : (
            editBtn
          ))}
        {withdrawRowBtn}
        {showDelete && <>{renderDeleteButton(groupRows, { includeSpinner: false })}</>}
      </div>
    );
  };

  return (
    <div className="documents-page att-daily-logs-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-journal-text" />}
        title={t("app.attendance.dailyLogs.title")}
        description={t("app.attendance.dailyLogs.subtitle")}
        rightContent={<AttendanceGuideButton onClick={() => setHelpKey("dailyLogs")} />}
      />

      <div className="att-page att-daily-page">
          <div className="att-daily-toolbar">
            <div className="att-daily-breadcrumb">
              <span>{t("app.attendance.nav.dailyLogs")}</span>
              <i className="bi bi-chevron-right" aria-hidden />
              <span className="att-daily-breadcrumb-current">{t("app.attendance.dailyLogs.breadcrumb")}</span>
            </div>
            <div className="att-daily-toolbar-right">
              <div className="att-daily-controls">
                <div className="att-daily-nav-row">
                  <label className="att-daily-label" htmlFor="att-daily-selected-date">
                    {t("app.attendance.dailyLogs.viewDay")}
                  </label>
                  <div className="att-daily-nav-btns">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary att-daily-nav-arrow"
                      onClick={() => setSelectedYmd((s) => addCalendarDaysToYmd(s, -1, displayTz))}
                      aria-label={t("app.attendance.dailyLogs.prevDay")}
                    >
                      <i className="bi bi-chevron-left" aria-hidden />
                    </button>
                    <input
                      id="att-daily-selected-date"
                      type="date"
                      className="att-daily-date-input att-daily-date-input--selected"
                      value={selectedYmd}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) return;
                        setSelectedYmd(v);
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary att-daily-nav-arrow"
                      onClick={() => setSelectedYmd((s) => addCalendarDaysToYmd(s, 1, displayTz))}
                      aria-label={t("app.attendance.dailyLogs.nextDay")}
                    >
                      <i className="bi bi-chevron-right" aria-hidden />
                    </button>
                  </div>
                  {!isToday && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary att-daily-today-btn"
                      onClick={() => setSelectedYmd(formatYmdInZone(Date.now(), displayTz))}
                    >
                      {t("app.attendance.dailyLogs.pickToday")}
                    </button>
                  )}
                </div>
              </div>
              <span className="att-daily-toolbar-date-hint">
                <i className="bi bi-info-circle me-1" aria-hidden />
                {t("app.attendance.dailyLogs.toolbarDateHint")}
              </span>
            </div>
          </div>

          <section className="att-daily-hero att-daily-hero-sui" aria-labelledby="att-daily-hero-heading">
            <Grid stackable columns={3} verticalAlign="top" className="att-daily-hero-grid">
              <Grid.Column mobile={16} tablet={8} computer={5} verticalAlign="top">
                <div className="att-daily-hero-date">
                  <div className="att-daily-hero-date-panel">
                    <div className="att-daily-hero-main">
                      <div className="att-daily-hero-text" id="att-daily-hero-heading">
                        {dateHeroParts ? (
                          <>
                            <span className="att-daily-hero-weekday">{dateHeroParts.weekday}</span>
                            <span className="att-daily-hero-calendar">{dateHeroParts.calendar}</span>
                          </>
                        ) : (
                          <span className="att-daily-hero-calendar">—</span>
                        )}
                      </div>
                      {isToday && <span className="att-chip att-daily-today-chip">{t("app.attendance.dailyLogs.todayBadge")}</span>}
                    </div>
                    <p className="att-daily-hero-tz">{t("app.attendance.dailyLogs.displayTimeZone", { tz: displayTz })}</p>
                  </div>
                </div>
              </Grid.Column>

              <Grid.Column mobile={16} tablet={8} computer={6} verticalAlign="top">
                <aside className="att-daily-summary-panel" aria-label={t("app.attendance.dailyLogs.shiftSummary")}>
                  <div className="att-daily-summary-title">{t("app.attendance.dailyLogs.shiftSummary")}</div>
                  <div className="att-daily-summary-grid">
                    <div className="att-daily-summary-item">
                      <span className="att-daily-summary-label">{t("app.attendance.dailyLogs.totalWorked")}</span>
                      <span className="att-daily-summary-value att-daily-summary-value--primary">
                        {loading ? "—" : `${totalHoursDisplay.toFixed(2)}h`}
                      </span>
                    </div>
                    <div className="att-daily-summary-item">
                      <span className="att-daily-summary-label">{t("app.attendance.dailyLogs.overtime")}</span>
                      <span className="att-daily-summary-value">
                        {loading
                          ? "—"
                          : overtimeDisplay != null && Number.isFinite(overtimeDisplay)
                            ? `${overtimeDisplay.toFixed(2)}h`
                            : "—"}
                      </span>
                    </div>
                    <div className="att-daily-summary-item att-daily-summary-item--wide">
                      <span className="att-daily-summary-label">{t("app.attendance.dailyLogs.heroLocation")}</span>
                      <span className="att-daily-summary-muted" title={locationSummary ?? undefined}>
                        {loading ? "—" : locationSummary ?? "—"}
                      </span>
                    </div>
                    <div className="att-daily-summary-item">
                      <span className="att-daily-summary-label">{t("app.attendance.dailyLogs.firstEntry")}</span>
                      <span className="att-daily-summary-value">
                        {loading ? "—" : firstEvent ? formatAttendanceTime(firstEvent.occurredAtUtc) : "—"}
                      </span>
                    </div>
                    <div className="att-daily-summary-item">
                      <span className="att-daily-summary-label">{t("app.attendance.dailyLogs.lastExit")}</span>
                      <span className="att-daily-summary-value">
                        {loading ? "—" : lastCheckOutEvent ? formatAttendanceTime(lastCheckOutEvent.occurredAtUtc) : "—"}
                      </span>
                    </div>
                    {!hidePunctuality && (
                      <div className="att-daily-summary-item att-daily-summary-item--wide">
                        <span className="att-daily-summary-label">{t("app.attendance.hub.punctuality")}</span>
                        <span
                          className={`att-daily-summary-value ${
                            punctualityKind === "onTime"
                              ? "att-daily-summary-value--punctuality-on"
                              : punctualityKind === "late"
                                ? "att-daily-summary-value--punctuality-late"
                                : ""
                          }`}
                        >
                          {loading
                            ? "—"
                            : punctualityKind === "onTime"
                              ? t("app.attendance.hub.punctualityOnTime")
                              : punctualityKind === "late"
                                ? t("app.attendance.hub.punctualityLate")
                                : "—"}
                        </span>
                        <span className="att-daily-summary-muted att-daily-summary-punctuality-hint">
                          {t("app.attendance.hub.punctualityHint")}
                        </span>
                      </div>
                    )}
                  </div>
                </aside>
              </Grid.Column>

              <Grid.Column mobile={16} tablet={16} computer={5} verticalAlign="top">
                <div className="att-daily-map-panel" aria-label={t("app.attendance.dailyLogs.mapTitle")}>
                  <div className="att-daily-map-panel-head">{t("app.attendance.dailyLogs.mapTitle")}</div>
                  <div className="att-daily-map-wrap att-daily-map-wrap--hero">
                    {lastMapCoords && !loading ? (
                      <>
                        <iframe
                          title={t("app.attendance.dailyLogs.mapHint")}
                          className="att-daily-map-iframe att-daily-map-iframe--hero"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://www.google.com/maps?q=${lastMapCoords.lat},${lastMapCoords.lng}&z=15&output=embed`}
                        />
                        <div className="att-map-badge">
                          <span className="att-map-dot" />
                          {t("app.attendance.dailyLogs.mapHint")}
                        </div>
                      </>
                    ) : (
                      <div className="att-map-placeholder att-daily-map-fallback att-daily-map-fallback--hero">
                        <p className="att-daily-map-fallback-text">{t("app.attendance.dailyLogs.mapNoLocation")}</p>
                        <div className="att-map-badge">
                          <span className="att-map-dot att-map-dot--muted" />
                          {t("app.attendance.dailyLogs.mapHint")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Grid.Column>
            </Grid>
          </section>

          <div className="att-daily-below-hero-hint">
            <p className="mb-0">
              <i className="bi bi-info-circle me-1" aria-hidden />
              {t("app.attendance.dailyLogs.streamActionsPolicy")}
            </p>
          </div>

          <div className="att-daily-main-row">
            <div className="w-100">
              <div className="att-daily-stream-head">
                <h3 className="att-section-title mb-0">{t("app.attendance.dailyLogs.streamTitle")}</h3>
              </div>
              <div className="att-table-card att-daily-stream-card">
                <TableContainer>
                  <table className="table att-daily-table mb-0">
                    <thead>
                      <tr>
                        <th scope="col" className="att-daily-th">
                          {t("app.attendance.dailyLogs.colType")}
                        </th>
                        <th scope="col" className="att-daily-th">
                          {t("app.attendance.dailyLogs.colTime")}
                        </th>
                        <th scope="col" className="att-daily-th att-daily-th-duration">
                          <span className="d-block">{t("app.attendance.dailyLogs.colDuration")}</span>
                          <span className="att-daily-th-duration-hint">{t("app.attendance.dailyLogs.colDurationHint")}</span>
                        </th>
                        <th scope="col" className="att-daily-th">
                          {t("app.attendance.dailyLogs.colProjects")}
                        </th>
                        <th scope="col" className="att-daily-th">
                          {t("app.attendance.dailyLogs.colLocation")}
                        </th>
                        <th scope="col" className="att-daily-th">
                          {t("app.attendance.dailyLogs.colNote")}
                        </th>
                        <th scope="col" className="att-daily-th att-daily-th-actions">
                          {t("app.attendance.dailyLogs.colActions")}
                        </th>
                      </tr>
                    </thead>
                    {loading ? (
                      <tbody>
                        <tr>
                          <td colSpan={colCount} className="att-daily-empty">
                            <span className="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden />
                            {t("app.common.loading")}
                          </td>
                        </tr>
                      </tbody>
                    ) : activityGroups.length === 0 ? (
                      <tbody>
                        <tr>
                          <td colSpan={colCount} className="att-daily-empty">
                            {t("app.attendance.hub.noEvents")}
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      activityGroups.map((group, gi) => {
                        const numGroups = activityGroups.length;
                        const isPairGroup =
                          group.length === 2 &&
                          group[0].ev.eventType === AttendanceEventType.CheckIn &&
                          group[1].ev.eventType === AttendanceEventType.CheckOut;
                        const groupKindLabel = isPairGroup
                          ? t("app.attendance.dailyLogs.groupKindPair")
                          : t("app.attendance.dailyLogs.groupKindSingle");
                        /* Newest-first list: number from bottom (oldest = Session 1) */
                        const sessionLabel = t("app.attendance.dailyLogs.groupSession", {
                          n: numGroups - gi,
                        });
                        const onlySessionIsLoneCheckIn =
                          numGroups === 1 &&
                          group.length === 1 &&
                          group[0].ev.eventType === AttendanceEventType.CheckIn;
                        const inFirstChronoSession = gi === numGroups - 1;
                        const inLastChronoSession = gi === 0;
                        /** @param {object} ev */
                        const canEditEvent = (ev) =>
                          Boolean(eventKey(ev)) &&
                          !onlySessionIsLoneCheckIn &&
                          (inFirstChronoSession || (inLastChronoSession && !newestSessionIsLoneCheckInOnly));

                        const showDeleteInBanner = group.some((r) => eventKey(r.ev));

                        return (
                        <tbody
                          key={`g-${gi}`}
                          className={`att-daily-group ${isPairGroup ? "att-daily-group--pair" : "att-daily-group--single"}`}
                        >
                          <tr className="att-daily-group-banner">
                            <td colSpan={6}>
                              <div className="att-daily-group-banner-inner">
                                <span className="att-daily-group-banner-title">
                                  <span className="att-daily-group-banner-session">{sessionLabel}</span>
                                  <span className="att-daily-group-banner-sep" aria-hidden>
                                    ·
                                  </span>
                                  <span className="att-daily-group-banner-kind">{groupKindLabel}</span>
                                </span>
                              </div>
                            </td>
                            <td className="att-daily-td att-daily-actions att-daily-group-banner-action-cell">
                              {showDeleteInBanner ? (
                                <div className="att-daily-action-btns">{renderDeleteButton(group)}</div>
                              ) : null}
                            </td>
                          </tr>
                          {group.map((row, ri) => {
                            const { ev, duration, durationRowSpan } = row;
                            const loc =
                              ev.location != null && String(ev.location).trim() !== "" ? String(ev.location).trim() : "—";
                            const noteOnly = noteTextWithoutCoordinates(ev.note);
                            const noteCell = noteOnly !== "" ? noteOnly : "—";
                            const eid = ev.id || `${ev.occurredAtUtc}-${ev.eventType}-${gi}-${ri}`;
                            const canEditThis = canEditEvent(ev);
                            const linkedIds = Array.isArray(ev.projectIds)
                              ? ev.projectIds.map((x) => String(x).trim()).filter(Boolean)
                              : ev.projectId
                                ? [String(ev.projectId).trim()]
                                : [];
                            const projectNames = linkedIds
                              .map((id) => {
                                const match = projects.find((p) => String(p.id) === id);
                                return match ? match.name : id;
                              })
                              .filter(Boolean);
                            const projectsCell = projectNames.length > 0 ? projectNames.join(", ") : "—";
                            return (
                              <tr key={eid}>
                                <td className="att-daily-td">
                                  <span
                                    className={
                                      ev.eventType === AttendanceEventType.CheckOut
                                        ? "att-daily-type att-daily-type--out"
                                        : "att-daily-type att-daily-type--in"
                                    }
                                  >
                                    {ev.eventType === AttendanceEventType.CheckOut
                                      ? t("app.attendance.hub.checkOut")
                                      : t("app.attendance.hub.checkIn")}
                                  </span>
                                </td>
                                <td className="att-daily-td att-daily-time">{formatAttendanceTime(ev.occurredAtUtc)}</td>
                                {durationRowSpan !== 0 && (
                                  <td
                                    rowSpan={durationRowSpan}
                                    className={`att-daily-td att-daily-duration ${durationRowSpan === 2 ? "att-daily-duration--pair" : ""}`}
                                  >
                                    <span className="att-daily-duration-inner">{duration}</span>
                                  </td>
                                )}
                                <td className="att-daily-td att-daily-cell-muted att-daily-td-projects" title={projectsCell}>
                                  {projectsCell}
                                </td>
                                <td className="att-daily-td att-daily-cell-muted att-daily-td-location">{loc}</td>
                                <td className="att-daily-td att-daily-note att-daily-td-note">{noteCell}</td>
                                <td className="att-daily-td att-daily-actions">
                                  {renderActionButtons(ev, {
                                    showEdit: canEditThis,
                                    showDelete: false,
                                    groupRows: group,
                                  })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        );
                      })
                    )}
                  </table>
                </TableContainer>
              </div>
            </div>
          </div>
      </div>

      {deleteModalGroupRows && (
          <div
            className="documents-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="att-daily-delete-dialog-title"
            onClick={(e) => {
              if (e.target === e.currentTarget && !deleteModalBusy) {
                setDeleteModalGroupRows(null);
              }
            }}
          >
            <div
              className="documents-modal documents-modal--upload-single documents-modal--delete-confirm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="documents-modal-preview" style={{ borderRight: "none", background: "#fff" }}>
                <div className="documents-upload-modal-header">
                  <div>
                    <p className="documents-upload-modal-eyebrow">{t("app.attendance.dailyLogs.deleteDialogEyebrow")}</p>
                    <h2 className="documents-upload-modal-title" id="att-daily-delete-dialog-title">
                      {t("app.attendance.dailyLogs.deleteDialogTitle")}
                    </h2>
                  </div>
                  <button
                    type="button"
                    className="btn btn-light documents-btn"
                    aria-label={t("app.common.close")}
                    disabled={deleteModalBusy}
                    onClick={() => setDeleteModalGroupRows(null)}
                  >
                    {t("app.common.close")}
                  </button>
                </div>

                <div className="documents-modal-sheet documents-modal-sheet--upload documents-delete-confirm-body">
                  <div className="documents-delete-confirm-summary">
                    <div className="documents-delete-confirm-icon-wrap" aria-hidden>
                      <i className="bi bi-trash3" />
                    </div>
                    <p className="documents-delete-confirm-lead">
                      {useDirectDelete
                        ? deleteModalRowsChrono.length > 1
                          ? t("app.attendance.dailyLogs.deleteDialogLeadPair")
                          : t("app.attendance.dailyLogs.deleteDialogLeadSingle")
                        : t("app.attendance.dailyLogs.deleteDialogLeadApproval")}
                    </p>
                  </div>

                  <div className="documents-delete-confirm-target">
                    <p className="documents-form-label documents-delete-confirm-target-label">
                      {t("app.attendance.dailyLogs.deleteDialogEventsLabel")}
                    </p>
                    <ul className="list-unstyled mb-0 att-daily-delete-dialog-events">
                      {deleteModalRowsChrono.map((row) => {
                        const ev = row.ev;
                        const isOut = ev.eventType === AttendanceEventType.CheckOut;
                        const k = eventKey(ev) || `${ev.occurredAtUtc}-${ev.eventType}`;
                        return (
                          <li key={k} className="att-daily-delete-dialog-line">
                            <span className="text-muted me-2">
                              {isOut ? t("app.attendance.hub.checkOut") : t("app.attendance.hub.checkIn")}
                            </span>
                            <span className="fw-semibold">{formatAttendanceTime(ev.occurredAtUtc)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="documents-modal-actions documents-delete-confirm-actions">
                    <button
                      type="button"
                      className="btn btn-light documents-btn"
                      disabled={deleteModalBusy}
                      onClick={() => setDeleteModalGroupRows(null)}
                    >
                      {t("app.attendance.dailyLogs.deleteDialogCancel")}
                    </button>
                    <button
                      type="button"
                      className="btn documents-btn documents-delete-confirm-btn"
                      disabled={deleteModalBusy}
                      onClick={handleDeleteModalConfirm}
                    >
                      {deleteModalBusy
                        ? useDirectDelete
                          ? t("app.attendance.dailyLogs.deleteDialogDeleting")
                          : t("app.attendance.dailyLogs.deleteDialogSubmittingApproval")
                        : useDirectDelete
                          ? t("app.attendance.dailyLogs.deleteDialogConfirm")
                          : t("app.attendance.dailyLogs.submitForApproval")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {withdrawModalEv && (
          <div
            className="att-modal-overlay"
            role="presentation"
            onMouseDown={(e) => e.target === e.currentTarget && !withdrawModalBusy && setWithdrawModalEv(null)}
          >
            <div className="att-modal" style={{ maxWidth: 420 }}>
              <div className="att-modal-header">
                <h2 className="att-modal-title mb-0">{t("app.attendance.dailyLogs.withdrawDialogTitle")}</h2>
                <button
                  type="button"
                  className="att-modal-close"
                  aria-label={t("app.common.close")}
                  disabled={withdrawModalBusy}
                  onClick={() => setWithdrawModalEv(null)}
                >
                  ×
                </button>
              </div>
              <div className="att-modal-body">
                <p className="mb-0 text-muted small">{t("app.attendance.dailyLogs.withdrawDialogLead")}</p>
              </div>
              <div className="att-modal-footer">
                <button
                  type="button"
                  className="att-btn att-btn--ghost"
                  disabled={withdrawModalBusy}
                  onClick={() => setWithdrawModalEv(null)}
                >
                  {t("app.common.cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={withdrawModalBusy}
                  onClick={confirmWithdrawChangeRequest}
                >
                  {withdrawModalBusy
                    ? t("app.attendance.dailyLogs.withdrawDialogSubmitting")
                    : t("app.attendance.dailyLogs.withdrawDialogConfirm")}
                </button>
              </div>
            </div>
          </div>
        )}

      <AttendanceSelfEditModal
        open={Boolean(editModalEvent)}
        onClose={() => setEditModalEvent(null)}
        event={editModalEvent}
        projects={projects}
        loading={Boolean(editModalEvent && actionBusyId === String(editModalEvent.id))}
        onSave={handleEditSubmit}
        submitForApproval={!useDirectEdit}
      />
      <AttendanceHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AttendanceDailyLogsPage;
